import { useEffect, useState, type FormEvent } from "react"
import { useAuth } from "@clerk/react"
import { useSignIn as useLegacySignIn } from "@clerk/react/legacy"
import { useNavigate } from "react-router-dom"

import { PasswordResetForm } from "@/pages/client-area/auth-forms"
import { LoginForm, LoginFrame } from "@/pages/client-area/login-card"
import {
  getAuthErrorMessage,
  isExistingSessionError,
  type PasswordResetCompletePayload,
  type Status,
} from "@/pages/client-area/auth-helpers"

/**
 * Página de login em módulo próprio: é a única tela pública do Quantum+ e não
 * precisa carregar o chunk da área do cliente.
 */
export function ClientLoginPage() {
  const navigate = useNavigate()
  const { isSignedIn } = useAuth()
  const { isLoaded, signIn, setActive } = useLegacySignIn()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [authMode, setAuthMode] = useState<"sign_in" | "reset_password">("sign_in")
  const [status, setStatus] = useState<Status>("idle")
  const [message, setMessage] = useState("")
  useEffect(() => {
    const previous = document.title
    document.title = "Entrar · Quantum+"
    return () => {
      document.title = previous
    }
  }, [])

  useEffect(() => {
    if (isSignedIn) {
      navigate("/dashboard", { replace: true })
    }
  }, [isSignedIn, navigate])

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (isSignedIn) {
      navigate("/dashboard", { replace: true })
      return
    }
    if (!isLoaded || status === "saving") return
    setStatus("saving")
    setMessage("")
    try {
      const identifier = email.trim()
      const secret = password.trim()
      const result = await signIn.create({
        strategy: "password",
        identifier,
        password: secret,
      })
      if (result.status !== "complete" || !result.createdSessionId) {
        setStatus("error")
        setMessage(
          `Complete as etapas pendentes do login no Clerk para continuar. Status: ${result.status ?? "indefinido"}.`
        )
        return
      }
      await setActive({
        session: result.createdSessionId,
        navigate: async ({ decorateUrl }) => {
          const destination = decorateUrl("/dashboard")
          if (destination.startsWith("https://")) {
            window.location.href = destination
            return
          }
          navigate(destination)
        },
      })
    } catch (error) {
      if (isExistingSessionError(error)) {
        navigate("/dashboard", { replace: true })
        return
      }
      setStatus("error")
      setMessage(getAuthErrorMessage(error, "Não foi possível entrar."))
      return
    }
    setStatus("success")
  }

  async function handlePasswordResetComplete({
    createdSessionId,
  }: PasswordResetCompletePayload) {
    setPassword("")
    if (!createdSessionId) {
      setAuthMode("sign_in")
      setStatus("success")
      setMessage("Senha alterada. Entre com a nova senha.")
      return
    }
    if (!setActive) {
      setAuthMode("sign_in")
      setStatus("success")
      setMessage("Senha alterada. Entre com a nova senha.")
      return
    }

    await setActive({
      session: createdSessionId,
      navigate: async ({ decorateUrl }) => {
        const destination = decorateUrl("/dashboard")
        if (destination.startsWith("https://")) {
          window.location.href = destination
          return
        }
        navigate(destination)
      },
    })
  }

  return (
    <LoginFrame>
      {authMode === "reset_password" ? (
        <PasswordResetForm
          email={email}
          isLoaded={isLoaded}
          onCancel={() => {
            setAuthMode("sign_in")
            setStatus("idle")
            setMessage("")
          }}
          onComplete={handlePasswordResetComplete}
          onEmailChange={setEmail}
          signIn={signIn}
        />
      ) : (
        <LoginForm
          email={email}
          password={password}
          showPassword={showPassword}
          status={status}
          message={message}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onToggleShowPassword={() => setShowPassword((current) => !current)}
          onForgotPassword={() => {
            setAuthMode("reset_password")
            setStatus("idle")
            setMessage("")
          }}
          onSubmit={(event) => void handleLogin(event)}
        />
      )}
    </LoginFrame>
  )
}
